import { isEmailValid } from './helpers';
import { AGENCY_INITIATED_SURVEY, SURVEY_QUESTIONS } from './surveyQuestions';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import endpoints from 'constants/endpoints';
import { safeAWSCallPost, safePostCall } from 'helpers/firebaseSetup';
import { parseZohoJsonOutput } from 'helpers/zoho/response-parsers';
import { showAlert } from 'state/actions/site';
import { base64ToFile } from 'helpers/files';

const BASE_INITIAL_VALUES = {
  hasAgreed: false,
  email: '',
  signature: {
    dataURL: '',
    isEmpty: true
  }
};

function getInitialValues(source) {
  const initialValues = {
    ...BASE_INITIAL_VALUES,
    signature: { ...BASE_INITIAL_VALUES.signature }
  };

  if (source === 'agencyTable') {
    const agencyFollowUps = {};
    AGENCY_INITIATED_SURVEY.forEach(q => {
      initialValues[q.id] = q.selectType === 'one' ? '' : [];
      q.options.forEach(o => {
        if (o.followUp) agencyFollowUps[`${q.id}_${o.id}`] = '';
      });
    });
    if (Object.keys(agencyFollowUps).length) {
      initialValues.followUps = agencyFollowUps;
    }
  } else if (source === 'agentInfo') {
    initialValues.newEmployer = '';
    initialValues.followUps = { q1_products: '', q1_training: '' };
    SURVEY_QUESTIONS.forEach(q => {
      initialValues[q.id] = q.selectType === 'one' ? '' : [];
    });
  }

  return initialValues;
}

export function getModalTitle(currentStep, isLoading) {
  if (isLoading) return 'Sending Information...';
  if (currentStep === 1) return 'Offboarding Acknowledgement';
  if (currentStep === 2) return 'Offboarding Survey';
}

const isQuestionValid = (question, formData) => {
  const value = formData[question.id];
  if (question.selectType === 'one') {
    if (!value) return false;
    const option = question.options.find(o => o.id === value);
    if (option?.followUp) {
      return !!formData.followUps?.[`${question.id}_${value}`]?.trim();
    }
    return true;
  }
  const selected = value || [];
  return selected.length >= 1 && selected.length <= 3;
};

function validateForm(currentStep, formData, source) {
  if (currentStep === 1) {
    const { hasAgreed, email, signature, newEmployer } = formData;
    const { dataURL, isEmpty } = signature;
    const baseValid =
      hasAgreed &&
      !!email.trim() &&
      isEmailValid(email) &&
      !!dataURL &&
      !isEmpty;
    if (source === 'agentInfo') {
      return (
        baseValid &&
        !!(newEmployer ?? '').trim() &&
        isEmailValid(newEmployer ?? '')
      );
    }
    return baseValid;
  }

  if (currentStep === 2) {
    if (source === 'agencyTable') {
      return AGENCY_INITIATED_SURVEY.every(question =>
        isQuestionValid(question, formData)
      );
    }
    return SURVEY_QUESTIONS.every(question =>
      isQuestionValid(question, formData)
    );
  }
}

const formatDateTimeForFile = (date = new Date()) =>
  date.toISOString().replace(/[:.]/g, '-');

const uploadFile = async ({ file, fileName, token, url }) => {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('token', token);
  formData.append('fileName', fileName);
  formData.append('url', url);

  const response = await safeAWSCallPost(
    '/appointments/file-upload',
    formData,
    false
  );

  if (!response) {
    console.error(response);
    throw new Error(`Upload failed for ${url}`);
  }

  return response;
};

export default function useOffboarding({
  onClose,
  source,
  selectedAgentId,
  hideReleaseApprovalQuestion,
  refreshUser,
  onSuccess
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState(() => getInitialValues(source));

  const isFormValid = useMemo(() => {
    return validateForm(currentStep, formData, source);
  }, [currentStep, formData, source]);

  const agentId = useSelector(state => state.user.mainZohoId);
  const dispatch = useDispatch();

  async function handleSend() {
    setIsLoading(true);
    const data = { ...formData };
    const questions =
      source === 'agentInfo' ? SURVEY_QUESTIONS : AGENCY_INITIATED_SURVEY;

    questions.forEach(question => {
      const value = data[question.id];

      if (question.selectType === 'one') {
        const option = question.options.find(o => o.id === value);

        if (option?.followUp) {
          data[`${question.id}followUp`] =
            data.followUps?.[`${question.id}_${value}`]?.trim() || null;
        }

        data[question.id] = option.label;
        return;
      }

      data[question.id] = question.options
        .filter(o => value.includes(o.id))
        .map(o => o.label);
    });

    data.offboardedAgentId = selectedAgentId;
    data.userAgentId = agentId;

    if (hideReleaseApprovalQuestion && source === 'agencyTable') {
      data.q6 = null;
    }

    try {
      const body = {
        isZohoRequest: true,
        endpoint: endpoints.zoho.createOffboardingRecord,
        ...data
      };

      const response = await safePostCall(endpoints.awsProxy.safePost, body);

      if (!response?.data?.details?.output) {
        throw new Error('Invalid offboarding response');
      }

      const {
        ok,
        message,
        data: parsedResponse
      } = parseZohoJsonOutput(response.data.details.output);

      if (!ok) {
        throw new Error(message || 'Offboarding creation failed');
      }

      const tokenResponse = await safePostCall(endpoints.awsProxy.safePost, {
        endpoint: endpoints.zoho.getPortalToken,
        isZohoRequest: true
      });

      const token = tokenResponse?.data?.details?.output;

      if (!token) {
        throw new Error('Token generation failed');
      }

      const fileName =
        source === 'agencyTable'
          ? `AgencyInitiatedOffboardingSignature_${formatDateTimeForFile()}.png`
          : `AgentInitiatedOffboardingSignature_${formatDateTimeForFile()}.png`;

      const file = await base64ToFile(data.signature.dataURL, fileName);

      try {
        await Promise.all([
          uploadFile({
            file,
            fileName,
            token,
            url: `https://www.zohoapis.com/crm/v7/Contacts/${agentId}/Attachments`
          }),
          uploadFile({
            file,
            fileName,
            token,
            url: `https://www.zohoapis.com/crm/v7/Offboarding_Records/${parsedResponse.offboarding_record_id}/Attachments`
          })
        ]);
      } catch (uploadError) {
        console.warn(
          '[handleSend] Upload failed (CORS in local?):',
          uploadError.message
        );
      }

      dispatch(
        showAlert({
          message: message || 'Offboarding created successfully',
          code: 'success'
        })
      );

      if (refreshUser) {
        try {
          await dispatch(refreshUser(() => {}, {}));
        } catch (refreshError) {
          console.error('Refresh after submit failed', refreshError);
        }
      }

      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error(error);
      dispatch(
        showAlert({
          message: error.message || 'Something went wrong. Try again later.',
          code: 'error'
        })
      );
    } finally {
      setIsLoading(false);
    }
  }

  return {
    currentStep,
    setCurrentStep,
    isLoading,
    formData,
    setFormData,
    isFormValid,
    handleSend
  };
}
